import dayjs from '@/plugins/dayjs'
import type { TimeModel } from '@vuepic/vue-datepicker'

export const oneHourInMS = 60 * 60 * 1000

// eslint-disable-next-line sonarjs/cognitive-complexity
export function toConsecutiveDateIntervals(dates: string[]): string {
  const parsedDates = dates.map(date => dayjs(date))

  let startDate, endDate: dayjs.Dayjs | null = null

  const intervals: Array<[dayjs.Dayjs, dayjs.Dayjs | null]> = []
  for (let i = 0; i < parsedDates.length; i++) {
    const currentDate = parsedDates[i]
    const prevDate = parsedDates[i - 1] ?? null

    if (startDate) {
      const diff = currentDate.diff(prevDate, 'day')
      if (diff > 1) {
        intervals.push([startDate, endDate ?? null ])

        if(i === parsedDates.length - 1) {
          intervals.push([currentDate, null])
        }
        else
        {
          startDate = currentDate
          endDate = null
        }
        continue
      }
    }

    if (!startDate) {
      startDate = currentDate
    } else {
      endDate = currentDate
    }

    if(i === parsedDates.length - 1) {
      intervals.push([startDate, endDate])
    }
  }

  return intervals.map(interval => `${interval[0].format('ddd D MMM')}` + (interval[1] ? ` - ${interval[1].format('ddd D MMM')}` : '')).join(', ')
}

export function convertDateToString(dateTime: Date): string {
  return dayjs(dateTime).format('YYYY-MM-DD')
}

export function convertDateToDateTimeString(dateTime: Date): string {
  return dayjs(dateTime).format('YYYY-MM-DD HH:mm:ss')
}

export function convertTimeModelToTime(time: TimeModel): string {
  return dayjs(time).format('HH:mm')
}

export function convertTimeModelToTimeWithTZ(time: TimeModel, tz?: string): string {
  return dayjs.utc(time).tz(tz).format('HH:mm')
}

export function convertTimeToTimeModelWithTZ(time: string, tz: string): TimeModel {
  const [hoursString, minutesString] = time.split(':')

  const hours = parseInt(hoursString, 10)
  const minutes = parseInt(minutesString, 10)

  return dayjs.utc({
    hours,
    minutes,
  }).tz(tz).toObject()
}

export function convertDateTimeToDateWithTZ(dateTime: string, tz: string): Date {
  return dayjs.utc(dateTime).tz(tz).toDate()
}

export function convertTimeModelToTimeObjectWithTZ(time: TimeModel, tz?: string): TimeModel {
  return dayjs.utc(time).tz(tz).toObject()
}

export function convertTimeWithTZ(time: string, tz?: string): string {
  const [hoursString, minutesString] = time.split(':')

  const hours = parseInt(hoursString, 10)
  const minutes = parseInt(minutesString, 10)

  return dayjs.utc({
    hours,
    minutes,
  }).tz(tz).format('HH:mm')
}

export function getDateDifference(date1: Date, date2: Date, unit: 'seconds' | 'minutes' | 'hours' | 'days'): number {
  const durationBetweenDates = dayjs.duration(dayjs(date2).diff(date1))

  if (unit === 'seconds')
    return Math.round(durationBetweenDates.asSeconds())
  else if (unit === 'minutes')
    return Math.round(durationBetweenDates.asMinutes())
  else if (unit === 'hours')
    return Math.round(durationBetweenDates.asHours())
  else if (unit === 'days')
    return Math.round(durationBetweenDates.asDays())
  else throw new Error('Invalid unit provided')
}

export function createDateFromTimeModel(timeModel: TimeModel) {
  const { hours, minutes, seconds } = timeModel

  const numericHours = Number(hours) || 0
  const numericMinutes = Number(minutes) || 0
  const numericSeconds = Number(seconds) || 0

  const date = new Date(0)

  date.setUTCHours(numericHours)
  date.setUTCMinutes(numericMinutes)
  date.setUTCSeconds(numericSeconds)

  return date
}

export function formatCurrency(amount: number): string {
  return `£${amount}`
}
